import { useAuth } from '@/lib/auth/auth';
import { citywideEventKeys } from '@/features/events/feed/useCitywideEvents';
import {
  flattenFollowingFeed,
  flattenNearbyFeed,
  followingFeedKeys,
  nearbyFeedKeys,
} from '@/features/events/feed/feedQuery';
import {
  type CommentsResponse,
  type FeedEvent,
  type FeedResponse,
} from '@/lib/api/apiTypes';
import { mapFeedEventToEventDetail } from '@/features/events/feed/eventDiscoveryMappers';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { type CommentView, type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';
import {
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

const EMPTY_COMMENTS: CommentView[] = [];

export type EventDetailData = {
  event: RadiousEventDetail;
  comments: CommentView[];
};

export const eventDetailKeys = {
  all: ['event-detail'] as const,
  detail: (
    id: string,
    params: {
      latitude: number;
      longitude: number;
    }
  ) => ['event-detail', id, params] as const,
  byId: (id: string) => ['event-detail', id] as const,
};

export function useEventDetail(id?: string) {
  const request = useAuth((state) => state.request);
  const origin = useUserOriginCoords();
  const queryClient = useQueryClient();
  const cachedDetail = id ? getCachedEventDetailData(queryClient, id) : undefined;
  const queryKey = id
    ? eventDetailKeys.detail(id, {
        latitude: origin.latitude,
        longitude: origin.longitude,
      })
    : eventDetailKeys.all;

  return useQuery({
    queryKey,
    enabled: Boolean(id),
    initialData: cachedDetail,
    queryFn: async () => {
      if (!id) {
        throw new Error('Missing event id');
      }

      const params = new URLSearchParams({
        origin_lat: String(origin.latitude),
        origin_lng: String(origin.longitude),
      });
      if (origin.source === 'gps') {
        params.set('lat', String(origin.latitude));
        params.set('lng', String(origin.longitude));
      }

      const eventRequest = request<RadiousEventDetail>(`/events/${id}?${params.toString()}`, {
        analyticsAction: 'load_event',
      }).then((loadedEvent) => {
        const event = normalizeEventDetailArrays(loadedEvent);
        queryClient.setQueryData<EventDetailData>(queryKey, (current) => ({
          event,
          comments: current?.comments ?? EMPTY_COMMENTS,
        }));
        return event;
      });
      const commentsRequest = request<CommentsResponse>(`/events/${id}/comments`).catch(() => ({
        comments: EMPTY_COMMENTS,
      }));
      const [event, commentsPayload] = await Promise.all([
        eventRequest,
        commentsRequest,
      ]);

      return {
        event,
        comments: commentsPayload.comments ?? EMPTY_COMMENTS,
      } satisfies EventDetailData;
    },
  });
}

function normalizeEventDetailArrays(event: RadiousEventDetail): RadiousEventDetail {
  return {
    ...event,
    attributes: event.attributes ?? [],
    attendee_preview: event.attendee_preview ?? [],
    poster_images: event.poster_images ?? [],
    tags: event.tags ?? [],
  };
}

function getCachedEventDetailData(queryClient: QueryClient, id: string) {
  const existingDetail = queryClient
    .getQueriesData<EventDetailData>({ queryKey: eventDetailKeys.byId(id) })
    .find(([, data]) => data?.event.id === id)?.[1];

  if (existingDetail) {
    return existingDetail;
  }

  const previewEvent = findCachedFeedEvent(queryClient, id);
  if (!previewEvent) {
    return undefined;
  }

  return {
    event: mapFeedEventToEventDetail(previewEvent),
    comments: [],
  } satisfies EventDetailData;
}

function findCachedFeedEvent(queryClient: QueryClient, id: string) {
  const nearbyEvents = flattenNearbyFeed(
    queryClient
      .getQueriesData<InfiniteData<FeedResponse>>({ queryKey: nearbyFeedKeys.all })
      .flatMap(([, data]) => data?.pages ?? [])
  );
  const followingEvents = flattenFollowingFeed(
    queryClient
      .getQueriesData<InfiniteData<FeedEvent[]>>({ queryKey: followingFeedKeys.all })
      .flatMap(([, data]) => data?.pages ?? [])
  );
  const citywideEvents = queryClient
    .getQueriesData<FeedEvent[]>({ queryKey: citywideEventKeys.all })
    .flatMap(([, data]) => data ?? []);

  return [...nearbyEvents, ...followingEvents, ...citywideEvents].find((event) => event.id === id);
}
